import { useState } from 'react';
import { regenerateAllPostHtml, warmCache } from '../../shared/api/posts';
import AdminSidebar from '../components/AdminSidebar';
import './PostHtmlRegenerate.css';

interface RegenerateResult {
  message: string;
  total: number;
  success: number;
  error: number;
  errors: Array<{ slug: string; lang?: string; error: string }>;
}

interface WarmCacheResult {
  message: string;
  total: number;
  success: number;
  error: number;
  errors: Array<{ url: string; error: string }>;
}

function PostHtmlRegenerate() {
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [regenerateResult, setRegenerateResult] = useState<RegenerateResult | null>(null);
  const [warmCacheLoading, setWarmCacheLoading] = useState(false);
  const [warmCacheResult, setWarmCacheResult] = useState<WarmCacheResult | null>(null);
  
  const handleRegenerate = async () => {
    if (!confirm('すべての公開記事のHTMLファイルを再生成しますか？')) {
      return;
    }
    
    setRegenerateLoading(true);
    setRegenerateResult(null);
    
    try {
      const response = await regenerateAllPostHtml();
      setRegenerateResult(response);
    } catch (error) {
      alert('エラーが発生しました');
      console.error('Error regenerating HTML files:', error);
    } finally {
      setRegenerateLoading(false);
    }
  };

  const handleWarmCache = async () => {
    if (!confirm('すべての公開記事のキャッシュをウォーミングしますか？')) {
      return;
    }
    
    setWarmCacheLoading(true);
    setWarmCacheResult(null);
    
    try {
      const response = await warmCache();
      setWarmCacheResult(response);
    } catch (error) {
      alert('エラーが発生しました');
      console.error('Error warming cache:', error);
    } finally {
      setWarmCacheLoading(false);
    }
  };
  
  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-layout__main">
        <div className="post-html-regenerate">
          <div className="post-html-regenerate__header">
            <h1 className="post-html-regenerate__title">メンテナンス</h1>
          </div>
          
          {/* OGP HTML再生成セクション */}
          <div className="post-html-regenerate__section">
            <h2 className="post-html-regenerate__section-title">OGP HTML再生成</h2>
            <div className="post-html-regenerate__content">
              <p className="post-html-regenerate__description">
                すべての公開記事のHTMLファイルを再生成します。
              </p>
              <button
                className="post-html-regenerate__button post-html-regenerate__button--primary"
                onClick={handleRegenerate}
                disabled={regenerateLoading || warmCacheLoading}
              >
                {regenerateLoading ? '処理中...' : '再生成を実行'}
              </button>
              {regenerateResult && (
                <div className="post-html-regenerate__result">
                  <div className="post-html-regenerate__result-summary">
                    <p className="post-html-regenerate__result-message">{regenerateResult.message}</p>
                    <p className="post-html-regenerate__result-stats">
                      完了: {regenerateResult.success}件成功、{regenerateResult.error}件失敗（全{regenerateResult.total}記事）
                    </p>
                  </div>
                  {regenerateResult.errors.length > 0 && (
                    <div className="post-html-regenerate__errors">
                      <h3 className="post-html-regenerate__errors-title">エラー詳細</h3>
                      <ul className="post-html-regenerate__errors-list">
                        {regenerateResult.errors.map((err, idx) => (
                          <li key={idx} className="post-html-regenerate__errors-item">
                            {err.slug}
                            {err.lang && ` (${err.lang})`}: {err.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* キャッシュウォーミングセクション */}
          <div className="post-html-regenerate__section">
            <h2 className="post-html-regenerate__section-title">キャッシュウォーミング</h2>
            <div className="post-html-regenerate__content">
              <p className="post-html-regenerate__description">
                すべての公開記事のCloudFrontキャッシュを事前に作成します。
              </p>
              <button
                className="post-html-regenerate__button post-html-regenerate__button--primary"
                onClick={handleWarmCache}
                disabled={regenerateLoading || warmCacheLoading}
              >
                {warmCacheLoading ? '処理中...' : 'キャッシュウォーミングを実行'}
              </button>
              {warmCacheResult && (
                <div className="post-html-regenerate__result">
                  <div className="post-html-regenerate__result-summary">
                    <p className="post-html-regenerate__result-message">{warmCacheResult.message}</p>
                    <p className="post-html-regenerate__result-stats">
                      完了: {warmCacheResult.success}件成功、{warmCacheResult.error}件失敗（全{warmCacheResult.total}URL）
                    </p>
                  </div>
                  {warmCacheResult.errors.length > 0 && (
                    <div className="post-html-regenerate__errors">
                      <h3 className="post-html-regenerate__errors-title">エラー詳細</h3>
                      <ul className="post-html-regenerate__errors-list">
                        {warmCacheResult.errors.map((err, idx) => (
                          <li key={idx} className="post-html-regenerate__errors-item">
                            {err.url}: {err.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default PostHtmlRegenerate;
